import { type FunctionComponent, type HTMLAttributes } from 'react';
import { isApplicable } from '../../../../helpers/product-helper/product.helper';
import { type ProductResourceVariantCard } from '../../../../types/product.types';
import { DropdownMenu } from '../../../common-components/dropdown-menu/dropdown-menu.component';
import { ProductStock } from '../../product-stock/product-stock.component';

/**
 * @typedef ProductVariantStatusProps
 */
export type ProductVariantStockProps = {
	title: string;
	count: number;
	labelClassName?: string;
	titleClassName?: string;
	isMadeToOrder?: boolean;
	showVariantName?: boolean;
	brand?: string;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Variant Status
 *
 */
export const ProductVariantStock: FunctionComponent<ProductVariantStockProps> = ({
	className = '',
	labelClassName = 'b',
	titleClassName = '',
	title,
	count,
	isMadeToOrder = false,
	showVariantName = true,
	brand
}) =>
	isApplicable(title) ? (
		<div className={`flex flex-row flex-wrap items-center ${className}`.trim()}>
			<span className={labelClassName}>Finish: </span>
			{showVariantName && (
				<span className={`ml2 ${titleClassName}`.trim()} data-automation="finish-name">
					{title}
				</span>
			)}
			{count > 0 && (
				<div className="flex flex-row flex-wrap items-center justify-start">
					{showVariantName && <span className="theme-emphasis mh1">-</span>}
					<ProductStock count={count} isMadeToOrder={isMadeToOrder} brand={brand} />
				</div>
			)}
		</div>
	) : null;

/**
 * @typedef ProductDropdownVariantProps
 */
export type ProductDropdownVariantProps<T = ProductResourceVariantCard> = {
	selectedVariant?: ProductResourceVariantCard;
	variants: T[];
	onVariantSelected: (selected: ProductResourceVariantCard) => void;
	label?: string;
	labelClass?: string;
	itemClass?: string;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Dropdown Variant Selector
 *
 * @param {ProductDropdownVariantProps} props
 */
export const ProductDropdownVariantSelector: FunctionComponent<ProductDropdownVariantProps> = ({
	className = '',
	selectedVariant,
	variants,
	onVariantSelected,
	label,
	labelClass = 'tl',
	itemClass = 'bn bg-transparent pa3 tl w-100'
}) => {
	if (!selectedVariant) {
		return null;
	}

	return variants.length > 1 ? (
		<div className={className} data-testid="variant-dropdown-selector">
			{label && <strong className={`dib mv1 w-100 ${labelClass}`}>{label}</strong>}
			<DropdownMenu shouldCloseOnChoice={true} title={selectedVariant.title}>
				{variants.map((variant, index) => (
					<button
						tabIndex={index}
						key={`${variant.title}-${variant.id}-${index}`}
						className={`pointer ${itemClass}`}
						onClick={onVariantSelected.bind(null, variant)}>
						{variant.title}
					</button>
				))}
			</DropdownMenu>
		</div>
	) : (
		<ProductVariantTitle className="f5 theme-grey-darker" title={selectedVariant.title} />
	);
};

/**
 * @typedef ProductVariantTitleProps
 */
export type ProductVariantTitleProps = {
	title: string;
	labelClassName?: string;
	titleClassName?: string;
	showLabel?: boolean;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Variant Title Component
 *
 * @param {ProductVariantTitleProps} props
 */
export const ProductVariantTitle: FunctionComponent<ProductVariantTitleProps> = ({
	className = 'f6 theme-grey mt1',
	labelClassName = '',
	titleClassName = 'b',
	title
}) => {
	return (
		<div className={`dib ${className}`}>
			<span className={labelClassName}>Finish:</span>
			<div className={`dib ml1 ${titleClassName}`.trim()}>{title}</div>
		</div>
	);
};
